import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import { setSelectMainMenu } from '../../modules/hompageSlice';
import { useAnimation, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { opacityDefault, opacityLeft } from '../../styles/animation';
import { initialProjects } from '../../data/projects';
import { categories, CATEGORY } from '../../util/type/projectType';
import { scrollToTop } from '../../util/scrollUtils';
import PAGE from '../../page/page';

//css
import '../../css/section/mainbody.scss';

const useObserver = (threshold, setPageIndex, index) => {
  const animation = useAnimation();
  const { ref, inView } = useInView({ threshold: threshold });

  useEffect(() => {
    if (inView) {
      animation.start('visible');
      setPageIndex(index); // 현재 보이는 페이지 인덱스 설정
    } else {
      animation.start('hidden');
    }
  }, [animation, inView, setPageIndex, index]);

  return { ref, animation };
};

function MainBody({}) {
  const { t } = useTranslation('projects');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectMainMenu = (menuName: string) => {
    dispatch(setSelectMainMenu(menuName));
    navigate(menuName);
    scrollToTop();
  };

  // 4p 카테고리 분류
  const [selectedCategory, setSelectedCategory] = useState(CATEGORY.DT);

  // 선택된 카테고리에 따라 프로젝트를 필터링하고 최신 순으로 정렬
  const filteredProjects = initialProjects
    .filter(
      (project) =>
        selectedCategory === CATEGORY.ALL ||
        project.category.includes(selectedCategory)
    )
    // .sort((a, b) => b.id - a.id) // id 값이 더 큰 것이 더 최신임
    .slice(0, 2); // 최신 2개의 프로젝트만 보여줌

  const [currentPageIndex, setCurrentPageIndex] = useState(0); // 현재 활성화된 페이지 인덱스 상태

  // 페이지 이름을 관리하는 배열
  const pageNames = ['Main', 'Mission', 'Solutions', 'Projects', 'Contact'];

  // 현재 페이지 인덱스에 따라 조건부로 페이지 이름을 선택
  const currentPageName = pageNames[currentPageIndex];

  // useObserver 호출 시, 각 페이지의 인덱스를 전달합니다.
  const { ref: refIntellectualize, animation: animationIntellectualize } =
    useObserver(0.5, setCurrentPageIndex, 0);
  const { ref: refMissionLayout0, animation: animationMissionLayout0 } =
    useObserver(0.5, setCurrentPageIndex, 1);
  const { ref: refMissionLayout1, animation: animationMissionLayout1 } =
    useObserver(0.5, setCurrentPageIndex, 1);
  const { ref: refMissionLayout2, animation: animationMissionLayout2 } =
    useObserver(0.5, setCurrentPageIndex, 1);
  const { ref: refSolutionLayout1, animation: animationSolutionLayout1 } =
    useObserver(0.5, setCurrentPageIndex, 2);
  const { ref: refSolutionLayout2, animation: animationSolutionLayout2 } =
    useObserver(0.1, setCurrentPageIndex, 2);
  const { ref: refProjectLayout, animation: animationProjectLayout } =
    useObserver(0.1, setCurrentPageIndex, 3);
  const { ref: refContactLayout, animation: animationContactLayout } =
    useObserver(0.3, setCurrentPageIndex, 4);

  // nav-dots 내의 span 태그에 조건부로 selected 클래스를 추가하는 로직
  const navDots = [...Array(5)].map((_, index) => (
    <span
      key={index}
      onClick={() => scrollToPage(index)}
      className={currentPageIndex === index ? 'selected' : ''}
    ></span>
  ));

  const observerElementRef = useRef(null);
  let lastScrollY = window.scrollY;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 스크롤이 아래로 진행될 때
            if (window.scrollY > lastScrollY) {
              setCurrentPageIndex((prevIndex) => prevIndex + 1);
            } else {
              // 스크롤이 위로 진행될 때
              setCurrentPageIndex((prevIndex) => Math.max(prevIndex - 1, 0));
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (observerElementRef.current) {
      observer.observe(observerElementRef.current);
    }

    return () => {
      if (observerElementRef.current) {
        observer.unobserve(observerElementRef.current);
      }
    };
  }, []);

  // 스크롤 이벤트 리스너에서 lastScrollY 업데이트
  useEffect(() => {
    const handleScroll = () => {
      lastScrollY = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 페이지 네비 기능
  const scrollToPage = (pageNumber) => {
    // 0은 greeting이라 추가 수정 필요
    if (pageNumber === 0) {
      // pageNumber가 0일 경우, 최상단으로 스크롤
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    const sectionIds = [
      'intellectualize',
      'missionLayout0',
      'solutionLayout1',
      'projectLayout',
      'contactLayout',
    ];

    const selectedId = sectionIds[pageNumber];
    if (selectedId) {
      const sectionElement = document.getElementById(selectedId);
      if (sectionElement) {
        sectionElement.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <main className="wrapper mainbody">
      <motion.div
        id="intellectualize"
        ref={refIntellectualize}
        animate={animationIntellectualize}
        className="greeting full"
      >
        <div className="layout">
          <div className="wrap-con">
            <div className="wrap-img">
              <span></span>
            </div>
            <div className="wrap-h1">
              <h1>
                <span className="h1">Intellectualize</span>
                <span className="scroll-down">
                  <span className="circle"></span>
                  <span className="text font-body">Scroll Down</span>
                </span>
              </h1>
              <h1>and Illustrate</h1>
              <h1>all Information</h1>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 첫번째 페이지 감지요소 */}
      <div ref={observerElementRef} style={{ height: '1px' }}></div>

      <div className="container cont-mission">
        <motion.div
          id="missionLayout0"
          ref={refMissionLayout0}
          initial="hidden"
          animate={animationMissionLayout0}
          variants={opacityLeft}
          className="wrap-con"
        >
          <div className="layout">
            <h2 className="title">Our Mission</h2>
            <h4 className="wrap-description">
              <Trans ns="main_body">
                우리 미션은 전세계의 데이터를 <span>디지털 트윈</span>을 통해
                체계화하고
                <br />
                누구나 편리하게 이용할 수 있도록 지식화하는 것입니다.
              </Trans>
            </h4>
            <div className="wrap-btn">
              <button
                className="btn-big"
                onClick={() => selectMainMenu(PAGE.ABOUT_US)}
              >
                About Us +
              </button>
            </div>
          </div>
        </motion.div>
        <div className="wrap-img">
          <div className="layout">
            <div className="con-motion">
              <motion.div
                ref={refMissionLayout1}
                initial="hidden"
                animate={animationMissionLayout1}
                variants={opacityDefault}
                className="con-img"
              >
                <svg
                  className="spinner"
                  xmlns="http://www.w3.org/2000/svg"
                  x="-268.2"
                  y="511.1"
                  viewBox="-2794 0 2000.2 1999.5"
                >
                  <defs>
                    <clipPath id="clip">
                      <path
                        className="map"
                        d="M-4424.7-.3C-4527.2 27-4668.7-15-4721 102c105.8-8.6 76.5 143.8 158 184.4 67.7-51 175.3-108.4 178.6-222.8 47.7-20.6-31-43.4-40.3-64zm2222.5 0C-2304.7 27-2446.2-15-2498.5 102c105.8-8.6 76.4 143.8 158 184.4 67.7-51 175.2-108.4 178.6-222.8 48-20.6-31-43.4-40-64zM-4785 19.2c-63.4 43-173.6 44.8-231.5 92.5v393.6c24 38.5 55.3 71 97.4 90.3 65 54 178 49.7 140 142.4 74 89 56 167.3 39 282.8 48 178.4 37-14 116-74.5 63-63 189-184.8 56-230.3-55-33.2-104-108-206-67.4-20-46.4-166-86.2-50-121.2 71 65.2 67-74.4 112-107.6 176-16.7 9-193.3-56-130.5 56 147.2-189-42-33-30.7 0-122.6 106 79 127-50.2-196-43.5 37-186-69-187.4zm2222.4 0C-2643.6 74-2801 62-2828 164c113 73-151 0-206.7 7.7-101.4 20.5-3.3 215-21 172.4 85.4-90 187.4-12 203.8 22 26 90 67 189 156 230 65 54 178 50 141 143 74 89 57 168 39 283 49 179 37-14 116-74 63-63 190-185 56-230-55-33-103-108-206-67-19-46-166-86-50-121 71 66 68-74 112-107 176-16 9-193-55-130 56 148-188-42-33-30 0-122 106 79 128-50-195-43 37-186-68-187zm-1311 77.4C-4006 152.4-3910 208.6-4059 251c18.8-33.7-4.3-96.6-138-56.8-130.4 80 17.6 190.4 17 68.5 28.6-41 96.8 54.3-7.7 69-87-43.7-84 104.7-153.5 99-11.4 108.6 123.8-50.2 151.4 23-27.8-98.4 68.4 84 75.5 32.6 112-15.5-87.6 60.4-111.4.6-75.2-51-216 89-146.2 168 85 42 161 28 163 141-39 105 92 216 145 68-14-68 116-210 60-218-117-69-59-190-3-13 178-16-75-130 85-88 87-4 86 196 155 48 64-92 56 129 84 127 91 75 87 44 68 12 20 22 91 2 35-66-35 28-43 49-37 61-12-19-29-37-32-32-43-81 11-44 73-134 35-126 46-45 109-128 115-62-57-103 87-133 95-15 26 133 112-9 73-18 170-40 147-71-90-51-185-27-282-51 0-23-301 24-188.2-43-36-78-181.3 53-250 27-83 88-126.2-6-27.5-59zm2190.8 15.8c-85 50.5-19 100.2-153.8 138.7 19-33-4.2-96-138-56-130.3 80 17.7 191 17.2 69 28.5-41 96.7 55-7.8 69-87-43-84 105-153.5 99-11.4 109 123.8-50 151.4 23-28-98 68.4 84 75.5 33 112-15-87.6 61-111.4 1-75.2-50-215.8 89-146 168 84.6 43 160.2 29 162.4 141-39 105 91.5 217 145 68-14-68 116.3-209 60.3-218-117-69-59.3-189-3-12 177.8-16-75.2-129 85-87 6 0 11.7 1 17 2V441c-7.4 6.4-32.5-19.3-2-21.4 1 2.3 1.5 4 2 6V192c-29.8.8-36.5-37.8 0-72.8zM-4379.3 223c-162 20.2 84.8 93.2 0 0zm2222.5 0c-162 20.2 84.8 93.2 0 0zm-2159.6 78c-49.5 60.2 96.7 121.6 0 0zm2222.5 0c-49 60.2 97 121.6 0 0zm-2254 29.7c-51 63.6 59 44 0 0zm2223 0c-51 63.6 59 44 0 0zm-1228 56.7c-96 25 38 42 0 0zm-745 15.4c142 1.6-55 89.4-1 2zm2223 0c142 1.6-55 89.4-1 2zm-2085 9.4c26 45.6-39 140.6 0 0zm2223 0c26 45.6-39 140.6 0 0zm-1690 4c-112 120.7 1 117.2 16 10l-15-9.4zm-477 2.4c20 49.4-41 2.8 0 0zm-305 37.3c-36 18 34 40 0 0zm2223 0c-36 18 34 40 0 0zm-1188 57c6 73 85 2 0 0zm-264 4c8 97 66 29 0 0zm-1310 17c-5 37 44 32 0 0zm2223 0c-6 37 43 32 0 0zm-581 14c43 95 204 26 33 13l-33-14zm-1679 15c-10 58 90 5 0 0zm2223 0c-10 58 90 5 0 0zm-2163 20c21 54 138 37 0 0zm2223 0c21 54 138 37 0 0zm-1029 118h-5c-65 128 59-1 6-1zm42 14c25 117 207 21 18 4l-17-5zm7 69c-99 15-166 182-54 155 64-23 194 98 182-65-53-122-58-40-128-91zm-505 7c-120 79 50 125.3 0 0zm2223 0c-120 79 50 125.3 0 0zm-1131 30c-56 45 71 46.3 0 0zm43 37c-58 44.4 73 32 0 0zm-386 56c-10 56-55 200 22 63.4 1-25-4-45-22-63z"
                      />
                    </clipPath>
                  </defs>
                  <circle cx="-1793.9" cy="999.8" r="611.2" />
                  <circle
                    cx="-2238.4"
                    cy="555.4"
                    r="599.2"
                    fill="#fff"
                    clipPath="url(#clip)"
                    transform="translate(444.495 448.4)"
                  />
                </svg>
              </motion.div>
            </div>
            <div className="con-motion">
              <motion.div
                ref={refMissionLayout2}
                initial="hidden"
                animate={animationMissionLayout2}
                variants={opacityDefault}
                className="con-img"
              >
                <div className="scene">
                  <svg
                    className="spinner"
                    xmlns="http://www.w3.org/2000/svg"
                    x="-268.2"
                    y="511.1"
                    viewBox="-2794 0 2000.2 1999.5"
                  >
                    <defs>
                      <clipPath id="clip">
                        <path
                          className="map"
                          d="M-4424.7-.3C-4527.2 27-4668.7-15-4721 102c105.8-8.6 76.5 143.8 158 184.4 67.7-51 175.3-108.4 178.6-222.8 47.7-20.6-31-43.4-40.3-64zm2222.5 0C-2304.7 27-2446.2-15-2498.5 102c105.8-8.6 76.4 143.8 158 184.4 67.7-51 175.2-108.4 178.6-222.8 48-20.6-31-43.4-40-64zM-4785 19.2c-63.4 43-173.6 44.8-231.5 92.5v393.6c24 38.5 55.3 71 97.4 90.3 65 54 178 49.7 140 142.4 74 89 56 167.3 39 282.8 48 178.4 37-14 116-74.5 63-63 189-184.8 56-230.3-55-33.2-104-108-206-67.4-20-46.4-166-86.2-50-121.2 71 65.2 67-74.4 112-107.6 176-16.7 9-193.3-56-130.5 56 147.2-189-42-33-30.7 0-122.6 106 79 127-50.2-196-43.5 37-186-69-187.4zm2222.4 0C-2643.6 74-2801 62-2828 164c113 73-151 0-206.7 7.7-101.4 20.5-3.3 215-21 172.4 85.4-90 187.4-12 203.8 22 26 90 67 189 156 230 65 54 178 50 141 143 74 89 57 168 39 283 49 179 37-14 116-74 63-63 190-185 56-230-55-33-103-108-206-67-19-46-166-86-50-121 71 66 68-74 112-107 176-16 9-193-55-130 56 148-188-42-33-30 0-122 106 79 128-50-195-43 37-186-68-187zm-1311 77.4C-4006 152.4-3910 208.6-4059 251c18.8-33.7-4.3-96.6-138-56.8-130.4 80 17.6 190.4 17 68.5 28.6-41 96.8 54.3-7.7 69-87-43.7-84 104.7-153.5 99-11.4 108.6 123.8-50.2 151.4 23-27.8-98.4 68.4 84 75.5 32.6 112-15.5-87.6 60.4-111.4.6-75.2-51-216 89-146.2 168 85 42 161 28 163 141-39 105 92 216 145 68-14-68 116-210 60-218-117-69-59-190-3-13 178-16-75-130 85-88 87-4 86 196 155 48 64-92 56 129 84 127 91 75 87 44 68 12 20 22 91 2 35-66-35 28-43 49-37 61-12-19-29-37-32-32-43-81 11-44 73-134 35-126 46-45 109-128 115-62-57-103 87-133 95-15 26 133 112-9 73-18 170-40 147-71-90-51-185-27-282-51 0-23-301 24-188.2-43-36-78-181.3 53-250 27-83 88-126.2-6-27.5-59zm2190.8 15.8c-85 50.5-19 100.2-153.8 138.7 19-33-4.2-96-138-56-130.3 80 17.7 191 17.2 69 28.5-41 96.7 55-7.8 69-87-43-84 105-153.5 99-11.4 109 123.8-50 151.4 23-28-98 68.4 84 75.5 33 112-15-87.6 61-111.4 1-75.2-50-215.8 89-146 168 84.6 43 160.2 29 162.4 141-39 105 91.5 217 145 68-14-68 116.3-209 60.3-218-117-69-59.3-189-3-12 177.8-16-75.2-129 85-87 6 0 11.7 1 17 2V441c-7.4 6.4-32.5-19.3-2-21.4 1 2.3 1.5 4 2 6V192c-29.8.8-36.5-37.8 0-72.8zM-4379.3 223c-162 20.2 84.8 93.2 0 0zm2222.5 0c-162 20.2 84.8 93.2 0 0zm-2159.6 78c-49.5 60.2 96.7 121.6 0 0zm2222.5 0c-49 60.2 97 121.6 0 0zm-2254 29.7c-51 63.6 59 44 0 0zm2223 0c-51 63.6 59 44 0 0zm-1228 56.7c-96 25 38 42 0 0zm-745 15.4c142 1.6-55 89.4-1 2zm2223 0c142 1.6-55 89.4-1 2zm-2085 9.4c26 45.6-39 140.6 0 0zm2223 0c26 45.6-39 140.6 0 0zm-1690 4c-112 120.7 1 117.2 16 10l-15-9.4zm-477 2.4c20 49.4-41 2.8 0 0zm-305 37.3c-36 18 34 40 0 0zm2223 0c-36 18 34 40 0 0zm-1188 57c6 73 85 2 0 0zm-264 4c8 97 66 29 0 0zm-1310 17c-5 37 44 32 0 0zm2223 0c-6 37 43 32 0 0zm-581 14c43 95 204 26 33 13l-33-14zm-1679 15c-10 58 90 5 0 0zm2223 0c-10 58 90 5 0 0zm-2163 20c21 54 138 37 0 0zm2223 0c21 54 138 37 0 0zm-1029 118h-5c-65 128 59-1 6-1zm42 14c25 117 207 21 18 4l-17-5zm7 69c-99 15-166 182-54 155 64-23 194 98 182-65-53-122-58-40-128-91zm-505 7c-120 79 50 125.3 0 0zm2223 0c-120 79 50 125.3 0 0zm-1131 30c-56 45 71 46.3 0 0zm43 37c-58 44.4 73 32 0 0zm-386 56c-10 56-55 200 22 63.4 1-25-4-45-22-63z"
                        />
                      </clipPath>
                    </defs>
                    <circle cx="-1793.9" cy="999.8" r="611.2" />
                    <circle
                      cx="-2238.4"
                      cy="555.4"
                      r="599.2"
                      fill="#fff"
                      clipPath="url(#clip)"
                      transform="translate(444.495 448.4)"
                    />
                  </svg>
                  <div className="cube" id="cube2">
                    <div className="cube-face  cube-face-front"></div>
                    <div className="cube-face  cube-face-back"></div>
                    <div className="cube-face  cube-face-left"></div>
                    <div className="cube-face  cube-face-right"></div>
                    <div className="cube-face  cube-face-top"></div>
                    <div className="cube-face  cube-face-bottom"></div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="container cont-solutions">
        <motion.div
          id="solutionLayout1"
          ref={refSolutionLayout1}
          initial="hidden"
          animate={animationSolutionLayout1}
          variants={opacityDefault}
          className="wrap-title"
        >
          <div className="layout">
            <h2 className="title">Our Solutions</h2>
            <h4 className="wrap-description">
              <Trans ns="main_body">
                우리는 다양한 솔루션을 제공하고 있습니다.
              </Trans>
            </h4>
          </div>
        </motion.div>

        <div className="con-motion">
          <motion.div
            ref={refSolutionLayout2}
            initial="hidden"
            animate={animationSolutionLayout2}
            variants={opacityDefault}
            className="wrap-cards"
          >
            <div className="layout">
              <ul className="wrap-card">
                <li
                  className="con-card"
                  onClick={() => selectMainMenu(PAGE.SOL_DT)}
                >
                  <div className="con-img">
                    <img src="images/img_digital_twin.png" alt="" />
                  </div>
                  <div className="wrap-description">
                    <h3>Digital Twin</h3>
                    <p>
                      <Trans ns="main_body">
                        데이터 기반의 <span>산업 현장 구축∙관제 기능</span>을
                        통해
                        <br />
                        생산성을 극대화하는 가상화 솔루션
                      </Trans>
                    </p>
                  </div>
                  <div className="con-btn">
                    <button>
                      <span className="arr-right"></span>
                    </button>
                  </div>
                </li>
                <li
                  className="con-card"
                  onClick={() => selectMainMenu(PAGE.SOL_M)}
                >
                  <div className="con-img">
                    <img src="images/img_metaverse.png" alt="" />
                  </div>
                  <div className="wrap-description">
                    <h3>Metaverse</h3>
                    <p>
                      <Trans ns="main_body">
                        시간과 공간에 구애받지 않는 메타버스 공간을 통해
                        <br />
                        <span>자유로운 체험형 콘텐츠</span>를 제공하는 솔루션
                      </Trans>
                    </p>
                  </div>
                  <div className="con-btn">
                    <button>
                      <span className="arr-right"></span>
                    </button>
                  </div>
                </li>
                <li
                  className="con-card"
                  onClick={() => selectMainMenu(PAGE.SOL_VE)}
                >
                  <div className="con-img">
                    <img src="images/img_virtual_exhibition.png" alt="" />
                  </div>
                  <div className="wrap-description">
                    <h3>Virtual Exhibition</h3>
                    <p>
                      <Trans ns="main_body">
                        이용자에게 최선의 <span>제품/공간</span>
                        <br />
                        <span> 경험</span>을 제공하는 가상 공간 구축 솔루션
                      </Trans>
                    </p>
                  </div>
                  <div className="con-btn">
                    <button>
                      <span className="arr-right"></span>
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container cont-projects">
        <div className="con-motion">
          <motion.div
            id="projectLayout"
            ref={refProjectLayout}
            initial="hidden"
            animate={animationProjectLayout}
            variants={opacityDefault}
            className="layout"
          >
            <div className="wrap-title">
              <h2 className="title">Projects</h2>
              <ul>
                <ul>
                  {categories.map((category, i) =>
                    category === CATEGORY.DT ||
                    category === CATEGORY.META ||
                    category === CATEGORY.VE ? (
                      <li
                        key={i}
                        className={`font-accent ${
                          selectedCategory === category ? 'selected' : ''
                        }`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        <span className="font-accent">{category}</span>
                      </li>
                    ) : null
                  )}
                </ul>
              </ul>
              <div className="wrap-btn">
                <button
                  className="btn-big"
                  onClick={() => selectMainMenu(PAGE.PROJECTS)}
                >
                  Projects +
                </button>
              </div>
            </div>
            <div className="wrap-cards">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className={`con-project-card ${index === 0 ? 'big' : 'big'}`}
                  initial={{ y: 50, opacity: 0 }} // 시작 위치 및 투명도 설정
                  animate={{ y: 0, opacity: 1 }} // 최종 위치 및 투명도 설정
                  transition={{ duration: 0.5 }} // 전환 시간 설정
                >
                  <div
                    key={project.id}
                    className={`con-project-card ${
                      index === 0 ? 'big' : 'big'
                    }`}
                  >
                    <div className="con-project-img">
                      <img src={project.image} alt={project.name} />
                    </div>
                    <div className="con-project-des">
                      <div className="wrap-project-text">
                        <p className="font-big">{t(project.name)}</p>
                        <p>{project.period}</p>
                      </div>
                      <div className="wrap-project-btn">
                        <span
                          className="arr-right"
                          onClick={() => selectMainMenu(PAGE.PROJECTS)}
                        ></span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              {/* <div className="wrap-card big">
                <div className="wrap-img">
                  <img src="/images/project/img_testimg.png" alt="" />
                </div>
                <div className="con-des">
                  <div className="wrap=-text">
                    <p className="font-accent">
                      디지털 트윈 기반 관제 / 시뮬레이션
                    </p>
                    <p>2024.01.01. - 01.01.</p>
                  </div>
                  <div className="wrap-btn">
                    <span className="arr-right"></span>
                  </div>
                </div>
              </div>
              <div className="con-project-card small">
                <div className="con-img">
                  <img src="/images/project/img_testimg.png" alt="" />
                </div>
                <div className="con-des">
                  <div className="wrap-text">
                    <p className="font-big">수자원공사</p>
                    <p>2024.01.01. - 01.01.</p>
                  </div>
                  <div className="wrap-btn">
                    <span className="arr-right"></span>
                  </div>
                </div>
              </div> */}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container cont-contact">
        <div className="con-motion">
          <motion.div
            id="contactLayout"
            ref={refContactLayout}
            initial="hidden"
            animate={animationContactLayout}
            variants={opacityDefault}
            className="layout"
          >
            <div>
              <h2 className="title">Contact US</h2>
              <h4 className="wrap-text">
                <Trans ns="main_body">
                  서울시 구로구 디지털로 26길 123 지플러스타워 5층 501호
                </Trans>
              </h4>
              <p></p>
              <p className="font-accent wrap-number">
                <span>
                  <span>T.</span>+82.2.6959.0341
                </span>
                <span>
                  <span>E.</span>customer@ontwins.com
                </span>
                <span>
                  <span>F.</span>0505-099-0314
                </span>
              </p>

              <div className="wrap-btn">
                <button
                  className="btn-big"
                  onClick={() => selectMainMenu(PAGE.CONTACT)}
                >
                  Contact +
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="nav-bottom">
        <span className="nav-title font-menu">{currentPageName}</span>
        <div className="nav-dots">{navDots}</div>
      </div>
    </main>
  );
}

export default React.memo(MainBody);
